import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight, GraduationCap, Clock, Award, FileText, Users,
  CheckCircle2, Handshake, Building2, BookOpen, Heart, Loader2,
  Shield, Star, Briefcase, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = {
  institution: [
    { icon: GraduationCap, title: 'Academic Credit Integration', desc: 'Students earn internship or service-learning credits through structured teaching experience.' },
    { icon: Clock, title: 'Flexible Scheduling', desc: 'Programs fit around academic calendars — semester-based commitments with flexible hours.' },
    { icon: Award, title: 'Official Certificates', desc: 'Students receive verified volunteer hour certificates and recommendation letters.' },
    { icon: FileText, title: 'Impact Documentation', desc: 'Detailed reports on student engagement for your department records and accreditation.' },
  ],
  volunteer: [
    { icon: BookOpen, title: 'Full Training Provided', desc: 'Comprehensive instructor training — no prior teaching experience required.' },
    { icon: Users, title: 'Real Classroom Experience', desc: 'Lead or co-teach coding classes with mentorship from experienced instructors.' },
    { icon: Star, title: 'Resume Building', desc: 'Gain teaching, leadership, and communication skills valued by employers.' },
    { icon: Heart, title: 'Community Impact', desc: 'Help underserved children discover coding and build future-ready skills.' },
  ],
  rsc: [
    { icon: Users, title: 'Diverse Instructor Pipeline', desc: 'Access talented, passionate students who bring fresh perspectives to our classrooms.' },
    { icon: Shield, title: 'Vetted & Trained', desc: 'All volunteers complete background checks and RSC\'s instructor training program.' },
    { icon: Building2, title: 'Scalable Programs', desc: 'Expand our reach to more schools and communities with a reliable volunteer base.' },
  ],
};

const tiers = [
  { name: 'Bronze', subtitle: 'Volunteer Pipeline', color: 'bg-amber-600', features: ['Volunteer placements', 'Service hour certificates', 'Basic training', 'Semester-based commitment'] },
  { name: 'Silver', subtitle: 'Instructor Pipeline + Internships', color: 'bg-gray-400', features: ['Everything in Bronze', 'Paid instructor positions', 'Academic credit integration', 'Faculty advisor support', 'Quarterly impact reports'] },
  { name: 'Gold', subtitle: 'Full Partnership', color: 'bg-yellow-500', features: ['Everything in Silver', 'Co-branded programs', 'Curriculum co-development', 'Research collaboration', 'Joint events & workshops', 'Priority placement'] },
];

const workflow = [
  { step: '1', title: 'Institution Applies', desc: 'Your department coordinator fills out the partnership application form.', who: 'Institution' },
  { step: '2', title: 'RSC Reviews & Approves', desc: 'We review the application and schedule a kickoff meeting within 5-7 business days.', who: 'Ready Set STEM' },
  { step: '3', title: 'Students Sign Up', desc: 'Students from your institution apply as volunteer instructors through our platform.', who: 'Students' },
  { step: '4', title: 'Training & Placement', desc: 'Volunteers complete our instructor training and are placed in classrooms.', who: 'Both' },
  { step: '5', title: 'Teach & Track Hours', desc: 'Volunteers teach/assist classes. Hours are logged and verified automatically.', who: 'Volunteers' },
  { step: '6', title: 'Certificates & Credit', desc: 'Upon completion, volunteers receive certificates. Hour reports sent to institution.', who: 'Both' },
];

export default function InstitutionPartnerPage() {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    institution_name: '', institution_type: '', website: '',
    coordinator_name: '', coordinator_email: '', coordinator_phone: '',
    coordinator_title: '', department: '',
    address: '', city: '', state: '', zip_code: '',
    offers_credit: false, offers_internship: false, offers_service_hours: true,
    min_hours_required: '', notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.institution_name || !formData.coordinator_name || !formData.coordinator_email || !formData.institution_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/institution-partnerships/apply`, {
        ...formData,
        min_hours_required: parseInt(formData.min_hours_required) || 0,
      });
      setSubmitted(true);
      toast.success('Partnership application submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container-app py-20 text-center">
        <div className="max-w-lg mx-auto">
          <div className="w-20 h-20 rounded-full bg-green-100 mx-auto mb-6 flex items-center justify-center">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4">Application Submitted!</h1>
          <p className="text-muted-foreground mb-8">Thank you for your interest in partnering with Ready Set STEM. Our partnerships team will review your application and contact you within 5-7 business days.</p>
          <Link to="/"><Button className="btn-primary">Return to Homepage</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="institution-partner-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6">
              <Handshake className="w-4 h-4" /> Institution Partnership Program
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
              Partner Your School<br />
              <span className="text-rsc-yellow">With Ready Set STEM</span>
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Connect your students with real-world teaching experience in STEM education.
              We provide the curriculum, training, and classrooms — your students gain invaluable
              experience while making a difference in children's lives.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={() => { setShowForm(true); document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                Apply for Partnership <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="#how-it-works">
                <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                  See How It Works
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-white border-b">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '15+', label: 'Partner Institutions' },
              { value: '200+', label: 'Student Volunteers' },
              { value: '5,000+', label: 'Hours Contributed' },
              { value: '98%', label: 'Would Recommend' },
            ].map((s, i) => (
              <div key={i}>
                <p className="text-3xl font-black text-rsc-navy">{s.value}</p>
                <p className="text-sm text-gray-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-Way Benefits */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Everyone Benefits</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Our partnership model creates a win-win-win for institutions, student volunteers, and Ready Set STEM.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Institution Benefits */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-rsc-navy">For Your Institution</h3>
                  <p className="text-sm text-gray-500">Colleges, Universities & High Schools</p>
                </div>
              </div>
              <div className="space-y-4">
                {benefits.institution.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm text-rsc-navy">{b.title}</p>
                        <p className="text-xs text-gray-600">{b.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Volunteer Benefits */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-rsc-navy">For Student Volunteers</h3>
                  <p className="text-sm text-gray-500">Gain experience while giving back</p>
                </div>
              </div>
              <div className="space-y-4">
                {benefits.volunteer.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm text-rsc-navy">{b.title}</p>
                        <p className="text-xs text-gray-600">{b.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RSC Benefits */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-rsc-navy">For Ready Set STEM</h3>
                  <p className="text-sm text-gray-500">Expanding our impact together</p>
                </div>
              </div>
              <div className="space-y-4">
                {benefits.rsc.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm text-rsc-navy">{b.title}</p>
                        <p className="text-xs text-gray-600">{b.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section-padding bg-rsc-navy text-white">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">How the Partnership Works</h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">A simple 6-step process from application to impact.</p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {workflow.map((w, i) => (
              <div key={i} className="flex gap-4 p-6 bg-white/5 rounded-xl border border-white/10">
                <div className="w-10 h-10 rounded-full bg-rsc-yellow text-rsc-navy font-bold flex items-center justify-center flex-shrink-0">
                  {w.step}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-white">{w.title}</h3>
                    <Badge variant="outline" className="text-xs text-white/60 border-white/20">{w.who}</Badge>
                  </div>
                  <p className="text-sm text-white/70">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Partnership Tiers</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Choose the partnership level that fits your institution's goals.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier, i) => (
              <Card key={i} className={`card-marketing ${i === 2 ? 'border-2 border-rsc-yellow ring-2 ring-rsc-yellow/20' : ''}`}>
                <CardContent className="p-8">
                  <div className={`w-12 h-12 rounded-xl ${tier.color} flex items-center justify-center mb-4`}>
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-rsc-navy mb-1">{tier.name}</h3>
                  <p className="text-sm text-gray-500 mb-6">{tier.subtitle}</p>
                  <ul className="space-y-2">
                    {tier.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" className="section-padding bg-gradient-to-br from-rsc-blue/5 to-rsc-yellow/5">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Apply for Partnership</h2>
              <p className="text-gray-600">Fill out the form below and our partnerships team will be in touch within 5-7 business days.</p>
            </div>

            <Card className="card-dashboard">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Institution Info */}
                  <div>
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Building2 className="w-5 h-5" /> Institution Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><Label>Institution Name *</Label><Input value={formData.institution_name} onChange={e => setFormData({...formData, institution_name: e.target.value})} required /></div>
                      <div><Label>Institution Type *</Label>
                        <Select value={formData.institution_type} onValueChange={v => setFormData({...formData, institution_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="university">University</SelectItem>
                            <SelectItem value="college">Community College</SelectItem>
                            <SelectItem value="high_school">High School</SelectItem>
                            <SelectItem value="vocational">Vocational School</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Department</Label><Input value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} placeholder="e.g., Computer Science" /></div>
                      <div><Label>Website</Label><Input value={formData.website} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="https://" /></div>
                    </div>
                  </div>

                  {/* Coordinator Info */}
                  <div>
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5" /> Coordinator Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><Label>Contact Name *</Label><Input value={formData.coordinator_name} onChange={e => setFormData({...formData, coordinator_name: e.target.value})} required /></div>
                      <div><Label>Email *</Label><Input type="email" value={formData.coordinator_email} onChange={e => setFormData({...formData, coordinator_email: e.target.value})} required /></div>
                      <div><Label>Phone</Label><Input value={formData.coordinator_phone} onChange={e => setFormData({...formData, coordinator_phone: e.target.value})} /></div>
                      <div><Label>Title</Label><Input value={formData.coordinator_title} onChange={e => setFormData({...formData, coordinator_title: e.target.value})} placeholder="e.g., Department Chair" /></div>
                    </div>
                  </div>

                  {/* What you offer */}
                  <div>
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5" /> What Your Institution Offers</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={formData.offers_service_hours} onCheckedChange={c => setFormData({...formData, offers_service_hours: c})} />
                        <Label>Students can earn community service hours</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={formData.offers_credit} onCheckedChange={c => setFormData({...formData, offers_credit: c})} />
                        <Label>Students can earn academic credit (internship/service-learning)</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={formData.offers_internship} onCheckedChange={c => setFormData({...formData, offers_internship: c})} />
                        <Label>This counts as a formal internship</Label>
                      </div>
                      {(formData.offers_credit || formData.offers_internship) && (
                        <div className="ml-8">
                          <Label>Minimum hours required for credit</Label>
                          <Input type="number" className="w-32" value={formData.min_hours_required} onChange={e => setFormData({...formData, min_hours_required: e.target.value})} placeholder="e.g., 40" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label>Additional Notes</Label>
                    <textarea className="w-full min-h-[100px] p-3 border rounded-md resize-none" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Tell us about your goals, number of students interested, preferred timeline, etc." />
                  </div>

                  <Button type="submit" className="btn-primary w-full text-lg py-6" disabled={submitting}>
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
                    Submit Partnership Application
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
